/**
 * Backend URL utility - normalizes and validates the backend URL
 */

const PRIVATE_LAN_HOST =
  /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3})$/;

const LOCAL_BACKEND_IN_ENV =
  /\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i;

/**
 * Normalize backend URL - adds https:// if protocol is missing
 * @param {string} url - The backend URL from environment variable
 * @returns {string} - Normalized URL with protocol
 */
export function normalizeBackendUrl(url) {
  if (!url) {
    // Empty string → same-origin `/api` (CRA dev proxy or Vercel rewrite)
    return '';
  }

  url = url.trim().replace(/\/$/, '');

  if (url && !url.match(/^https?:\/\//) && !url.startsWith('/')) {
    if (url.includes('.railway.app') || url.includes('.vercel.app') || url.includes('.netlify.app') || url.includes('.fun')) {
      url = `https://${url}`;
    } else {
      url = `http://${url}`;
    }
  }

  return url;
}

/**
 * Resolve API base URL for the current page.
 * When the dev app is opened from a phone via http://192.168.x.x:3000, never
 * call http://localhost:8001 — use same-origin so setupProxy forwards to the Mac.
 */
export function getBackendUrl() {
  const envUrl = normalizeBackendUrl(process.env.REACT_APP_BACKEND_URL || '');

  if (typeof window === 'undefined') {
    return envUrl;
  }

  const { hostname } = window.location;
  const pageIsLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const pageIsLanDev = PRIVATE_LAN_HOST.test(hostname) && !pageIsLocalhost;

  // If the environment URL points to a specific external/LAN IP instead of localhost,
  // we should just use it directly, rather than proxying.
  if (envUrl && !LOCAL_BACKEND_IN_ENV.test(envUrl)) {
    return envUrl;
  }

  if (pageIsLanDev) {
    // Phone/tablet on Wi‑Fi: always use the dev-server /api proxy on :3000
    return '';
  }

  if (!pageIsLocalhost && LOCAL_BACKEND_IN_ENV.test(envUrl)) {
    return '';
  }

  return envUrl;
}

/** @deprecated Prefer getBackendUrl() in new code; kept for existing imports */
export const BACKEND_URL = getBackendUrl();
